import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
} from '@mui/material'
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'

import UserDataGrid from '../../components/Users/UserDataGrid'
import UserFilters from '../../components/Users/UserFilters'
import UserActionDialog from '../../components/Users/UserActionDialog'
import UserDetailsDialog from '../../components/Users/UserDetailsDialog'
import { useUsers } from '../../hooks/useUsers'

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    status: 'all',
    creditRange: [0, 1000],
    dateRange: null,
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, user: null })
  const [detailsDialog, setDetailsDialog] = useState({ open: false, user: null })

  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useUsers({
    search: searchTerm,
    filters,
    page: 1,
    limit: 25,
  })

  const handleSearch = (value) => {
    setSearchTerm(value)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleUserAction = (action, user) => {
    setActionDialog({
      open: true,
      type: action,
      user,
    })
  }

  const handleViewDetails = (user) => {
    setDetailsDialog({
      open: true,
      user,
    })
  }

  const handleExport = async () => {
    try {
      // TODO: Implement export functionality
      console.log('Exporting users...')
    } catch (error) {
      console.error('Export error:', error)
    }
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', color: 'success' },
      suspended: { label: 'Askıda', color: 'warning' },
      banned: { label: 'Yasaklı', color: 'error' },
      pending: { label: 'Beklemede', color: 'info' },
    }
    
    const config = statusConfig[status] || { label: status, color: 'default' }
    return <Chip label={config.label} color={config.color} size="small" />
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Kullanıcı Yönetimi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tüm kullanıcıları görüntüleyin ve yönetin
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              Yenile
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Dışa Aktar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleUserAction('create', null)}
            >
              Yeni Kullanıcı
            </Button>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Kullanıcı ara (email, ID...)"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtreler
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Filter Panel */}
        {showFilters && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <UserFilters
                filters={filters}
                onChange={handleFilterChange}
                onReset={() => setFilters({
                  status: 'all',
                  creditRange: [0, 1000],
                  dateRange: null,
                })}
              />
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                {usersData?.stats?.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Kullanıcı
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                {usersData?.stats?.active || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aktif Kullanıcı
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                {usersData?.stats?.suspended || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Askıdaki Kullanıcı
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                ${usersData?.stats?.totalCredits || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Kredi
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Data Grid */}
      <Card>
        <UserDataGrid
          users={usersData?.users || []}
          isLoading={isLoading}
          error={error}
          onUserAction={handleUserAction}
          onViewDetails={handleViewDetails}
          pagination={{
            total: usersData?.total || 0,
            page: usersData?.page || 1,
            limit: usersData?.limit || 25,
          }}
        />
      </Card>

      {/* Action Dialog */}
      <UserActionDialog
        open={actionDialog.open}
        type={actionDialog.type}
        user={actionDialog.user}
        onClose={() => setActionDialog({ open: false, type: null, user: null })}
        onSuccess={() => {
          refetch()
          setActionDialog({ open: false, type: null, user: null })
        }}
      />

      {/* User Details Dialog */}
      <UserDetailsDialog
        open={detailsDialog.open}
        user={detailsDialog.user}
        onClose={() => setDetailsDialog({ open: false, user: null })}
        onUserAction={handleUserAction}
      />
    </Box>
  )
}

export default UserManagement